import { type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { Icon, type IconTypes, LoadingIndicator } from '@ics-portal/guardian';
import { GET_PROJECTS_STATS } from '@api/queries/projects';
import { useMapContext } from '@pages/map/_utils/map-context';
import s from '../project-details-box.module.scss';

interface ProjectDetailsStatProps {
    label: string;
    icon: IconTypes;
    color: string;
    value?: number;
    loading?: boolean;
}

function ProjectDetailsStat({
    label,
    icon,
    color,
    value,
    loading,
}: ProjectDetailsStatProps): ReactNode {
    return loading ? (
        <LoadingIndicator variant="ring" />
    ) : (
        <div className={`flex flex-column gap-1 ${s.stats} ${color}`}>
            <Icon name={icon} color={color} fontSize="normal" />
            <h2>{value}</h2>
            <div>{label}</div>
        </div>
    );
}

export function ProjectDetailsStats(): ReactNode {
    const { project } = useMapContext();

    const { data, loading } = useQuery(GET_PROJECTS_STATS, {
        skip: !project,
        variables: {
            pid: project?.id ?? '',
        },
    });
    if (!project) {
        return null;
    }

    if (loading) {
        return <LoadingIndicator variant="ripple" />;
    }
    return (
        <div className="flex flex-row">
            <ProjectDetailsStat
                label="pending tasks"
                icon="report"
                color={s.red}
                value={data?.pendingTasks?.totalCount}
            />
            <ProjectDetailsStat
                label="assets"
                icon="apps"
                color={s.yellow}
                value={data?.assets.totalCount}
            />
            <ProjectDetailsStat
                label="components"
                icon="shapes"
                color={s.blue}
                value={data?.components.totalCount}
            />
        </div>
    );
}
